import { useCallback, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from './Auth';
import { useChat } from '../hooks/useChat';
import { useRecentConversations } from '../hooks/useRecentConversations';
import { Layout } from './Layout';
import { MessageList } from './Chat/MessageList';
import { ChatInput } from './Chat/ChatInput';
import { generateUUID } from '../lib/utils';

// UUID v4 validation regex
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const ChatPage = () => {
  const { chatId: rawChatId } = useParams<{ chatId?: string }>();

  // Validate chatId is a valid UUID, otherwise treat as undefined
  const chatId = useMemo(() => {
    if (!rawChatId) return undefined;
    return UUID_REGEX.test(rawChatId) ? rawChatId : undefined;
  }, [rawChatId]);
  const navigate = useNavigate();
  const location = useLocation();

  // Input height state - track if input is more than 4 rows
  const [isInputTall, setIsInputTall] = useState(false);

  // Shared input text state for both chat input instances
  const [inputText, setInputText] = useState('');

  // Get user info (no auth in demo mode)
  const { user, getAuthHeader } = useAuth();

  // Recent conversations for sidebar navigation
  const {
    conversations: recentConversations,
    addOrUpdate: addRecentConversation,
  } = useRecentConversations();

  /**
   * Wrapper to convert auth header format for useChat
   * Memoized to prevent unnecessary re-renders
   */
  const getAuthHeaderForChat = useCallback((): Record<string, string> => {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      return {};
    }
    return { Authorization: authHeader };
  }, [getAuthHeader]);

  // Get chat state - pass getAuthHeader for history fetching
  const {
    messages,
    isLoading: chatLoading,
    isLoadingHistory,
    error,
    streamingState,
    sendMessage,
    cancelStreaming,
  } = useChat({
    chatId,
    getAuthHeader: getAuthHeaderForChat,
  });

  // Derive whether this chatId is already tracked from existing data
  // This avoids needing to store tracking state separately
  const isAlreadyTracked = recentConversations.some((c) => c.chatId === chatId);

  // Track conversation in recent list when first user message appears
  // No setState needed - we derive tracking from recentConversations
  useEffect(() => {
    const firstUserMessage = messages.find((m) => m.role === 'user');
    if (chatId && firstUserMessage && !isAlreadyTracked) {
      addRecentConversation(chatId, firstUserMessage.content);
    }
  }, [chatId, messages, isAlreadyTracked, addRecentConversation]);

  // Handle pending message from navigation state
  // No tracking state needed - navigate() clears location.state synchronously,
  // preventing re-execution on subsequent renders
  useEffect(() => {
    const state = location.state as { pendingMessage?: string } | null;
    if (state?.pendingMessage && chatId) {
      sendMessage(state.pendingMessage, getAuthHeaderForChat);
      // Clear the navigation state - this prevents the effect from re-running
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    chatId,
    location.state,
    location.pathname,
    navigate,
    sendMessage,
    getAuthHeaderForChat,
  ]);

  /**
   * Handle sending a message
   * Navigate to chat URL if this is the first message
   */
  const handleSendMessage = useCallback(
    async (content: string) => {
      // If we're on the blank page (no chatId), navigate to new chat with the message in state
      if (!chatId && messages.length === 0) {
        const newChatId = generateUUID();
        // No need to reset tracking - new chatId will be different
        navigate(`/chats/${newChatId}`, {
          replace: true,
          state: { pendingMessage: content },
        });
        return;
      }

      await sendMessage(content, getAuthHeaderForChat);
    },
    [chatId, messages.length, navigate, sendMessage, getAuthHeaderForChat],
  );

  /**
   * Handle starting a new chat
   * Navigate to blank page - useChat's state-during-render pattern
   * automatically clears state when chatId changes
   */
  const handleNewChat = useCallback(() => {
    navigate('/');
  }, [navigate]);

  /**
   * Handle selecting a conversation from the recent list
   */
  const handleSelectConversation = useCallback(
    (selectedChatId: string) => {
      if (selectedChatId !== chatId) {
        navigate(`/chats/${selectedChatId}`);
      }
    },
    [chatId, navigate],
  );

  // Chat input component with shared state
  const chatInputComponent = (
    <ChatInput
      onSend={(message) => {
        handleSendMessage(message);
        setInputText(''); // Clear input after sending
      }}
      disabled={chatLoading}
      onInputHeightChange={setIsInputTall}
      value={inputText}
      onInputChange={setInputText}
    />
  );

  // Main authenticated app
  return (
    <Layout
      userEmail={user?.email}
      onNewChat={handleNewChat}
      recentConversations={recentConversations}
      onSelectConversation={handleSelectConversation}
      currentChatId={chatId}
    >
      {/* Scrollable Message List */}
      <MessageList
        messages={messages}
        isLoading={chatLoading}
        isLoadingHistory={isLoadingHistory}
        onSendPrompt={handleSendMessage}
        hidePrompts={isInputTall}
        emptyStateChatInput={
          messages.length === 0 && !isLoadingHistory
            ? chatInputComponent
            : undefined
        }
        userEmail={user?.email}
        streamingState={streamingState}
        onCancelStreaming={cancelStreaming}
      />

      {/* Error Display - Outside scroll container */}
      {error && (
        <div
          className="w-full flex justify-center flex-shrink-0"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-3xl mx-4 mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
          >
            <p className="text-sm text-red-800 dark:text-red-300">
              <span className="font-semibold">Error:</span> {error}
            </p>
          </motion.div>
        </div>
      )}

      {/* Composer / Chat Input - Bottom position */}
      {/* Show on mobile always, on desktop only when there are messages */}
      <div
        className={`flex justify-center w-full flex-shrink-0 ${
          messages.length === 0 ? 'sm:hidden' : ''
        }`}
      >
        <div className="w-full max-w-3xl">{chatInputComponent}</div>
      </div>
    </Layout>
  );
};
